import * as Yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.js';
import { IBAN_MAX_LENGTH } from '~/constants/common.ts';
import { regularExpressions } from '~/constants/regularExpressions.ts';

export const positiveNumberFop = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .test('is-non-negative', errorMessages.MUST_BE_GREATER_THAN_ZERO, (value) => {
    if (!value) return false;
    if (value.trim().startsWith('-')) return false;
    const num = Number(value);
    if (Number.isNaN(num)) return true;
    return num >= 0;
  })
  .test('no-comma', errorMessages.USE_COMMA, (value) => !value?.includes(','))
  .test('only-digits-and-dot', errorMessages.MUST_BE_NUMBER, (value) => {
    if (!value) return false;
    if (!regularExpressions.JUST_DOT.test(value)) return false;
    if ((value.match(/\./g) || []).length > 1) return false;
    return true;
  })
  .test('no-leading-zeros', errorMessages.MUST_BE_WITHOUT_ZEROES, (value) => {
    if (!value) return false;
    return !/^0\d/.test(value);
  })
  .test('max-2-decimals', errorMessages.MAX_LENGTH_OF_CHARACTERS(2), (value) => {
    if (!value) return false;
    const parts = value.split('.');
    const dec = parts[1];
    if (dec && dec.length > 2) return false;
    return true;
  });

export const multipleOfZero = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .test('no-comma', errorMessages.USE_COMMA, (value) => !value?.includes(','))
  .test('only-digits-and-dot', errorMessages.MUST_BE_NUMBER, (value) => {
    if (!value) return false;
    if (!/^-?\d*\.?\d*$/.test(value)) return false;
    if ((value.match(/\./g) || []).length > 1) return false;
    if ((value.match(/-/g) || []).length > 1) return false;
    if (value.includes('-') && !value.startsWith('-')) return false;
    return true;
  })
  .test('no-leading-zeros', errorMessages.MUST_BE_WITHOUT_ZEROES, (value) => {
    if (!value) return false;
    return !/^0\d/.test(value);
  })
  .test('max-2-decimals', errorMessages.MAX_LENGTH_OF_CHARACTERS(2), (value) => {
    if (!value) return false;
    const parts = value.split('.');
    const dec = parts[1];
    if (dec && dec.length > 2) return false;
    return true;
  })
  .test('is-non-negative', errorMessages.MUST_BE_POSITIVE_OR_ZERO, (value) => {
    if (!value) return false;
    if (value.trim().startsWith('-')) return false;
    const num = Number(value);
    if (Number.isNaN(num)) return true;
    return num >= 0;
  });

export const numberWithoutLeadingZero = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .test('no-comma', errorMessages.USE_COMMA, (value) => !value?.includes(','))
  .test('only-digits-and-dot', errorMessages.MUST_BE_NUMBER, (value) => {
    if (!value) return false;
    if (!/^-?\d*\.?\d*$/.test(value)) return false;
    if ((value.match(/\./g) || []).length > 1) return false;
    if ((value.match(/-/g) || []).length > 1) return false;
    if (value.includes('-') && !value.startsWith('-')) return false;
    return true;
  })
  .test('max-2-decimals', errorMessages.MAX_LENGTH_OF_CHARACTERS(2), (value) => {
    if (!value) return false;
    const parts = value.split('.');
    const dec = parts[1];
    if (dec && dec.length > 2) return false;
    return true;
  })
  .test('no-leading-zeros', errorMessages.MUST_BE_WITHOUT_ZEROES, (value) => {
    if (!value) return false;
    return !/^0\d/.test(value);
  });

export const monthlyInflowsValid = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .test('no-comma', errorMessages.USE_COMMA, (value) => !value?.includes(','))
  .test('only-digits-and-dot', errorMessages.MUST_BE_NUMBER, (value) => {
    if (!value) return false;
    if (!/^-?\d*\.?\d*$/.test(value)) return false;
    if ((value.match(/\./g) || []).length > 1) return false;
    if ((value.match(/-/g) || []).length > 1) return false;
    if (value.includes('-') && !value.startsWith('-')) return false;
    return true;
  })
  .test('no-leading-zeros', errorMessages.MUST_BE_WITHOUT_ZEROES, (value) => {
    if (!value) return false;
    return !/^0\d/.test(value);
  })
  .test('greater-than-zero', errorMessages.MUST_BE_GREATER_THAN_ZERO, (value) => {
    if (!value) return false;
    if (Number.isNaN(+value)) return true;
    return +value > 0;
  })
  .test('max-2-decimals', errorMessages.MAX_LENGTH_OF_CHARACTERS(2), (value) => {
    if (!value) return false;
    const parts = value.split('.');
    const dec = parts[1];
    if (dec && dec.length > 2) return false;
    return true;
  });

export const bankAccountsValidationSchema = Yup.array()
  .of(
    Yup.object().shape({
      accountNumber: Yup.string()
        .required(errorMessages.FIELD_REQUIRED)
        .length(IBAN_MAX_LENGTH, errorMessages.INCOMPLETE_IBAN),
      bankName: Yup.string().nullable(),
      bankMFO: Yup.string().nullable(),
      currency: Yup.object()
        .shape({
          value: Yup.string().nullable(),
          label: Yup.string().required(errorMessages.FIELD_REQUIRED),
        })
        .nullable()
        .required(errorMessages.FIELD_REQUIRED),
    })
  )
  .test('unique-bank-accounts', errorMessages.DUPLICATE_ACCOUNT_NUMBER, function (accounts) {
    if (!accounts) return true;
    const errors: Yup.ValidationError[] = [];
    accounts.forEach((account, index) => {
      const isDuplicate = accounts.some(
        (other, otherIndex) =>
          otherIndex !== index &&
          other.accountNumber === account.accountNumber &&
          other.currency?.value === account.currency?.value
      );
      if (isDuplicate) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].accountNumber`,
            message: errorMessages.DUPLICATE_ACCOUNT_NUMBER,
          })
        );
      }
    });
    if (errors.length > 0) throw new Yup.ValidationError(errors);
    return true;
  })
  .test('iban-validation', errorMessages.INVALID_IBAN, function (accounts) {
    if (!accounts) return true;
    const errors: Yup.ValidationError[] = [];
    accounts.forEach((account, index) => {
      const hasAccountNumber = !!account.accountNumber;
      const hasBankName = !!account.bankName;
      const hasBankMFO = !!account.bankMFO;

      if (hasAccountNumber && (!hasBankName || !hasBankMFO)) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].accountNumber`,
            message: errorMessages.INVALID_IBAN,
          })
        );
      }
    });
    if (errors.length > 0) throw new Yup.ValidationError(errors);
    return true;
  });

export const fundingSourcesObject = Yup.object()
  .shape({
    businessActivity: Yup.boolean(),
    financialAssistance: Yup.boolean(),
    contractAndDerivatives: Yup.boolean(),
    loan: Yup.boolean(),
    saleOfReceivables: Yup.boolean(),
    saleOfSecurities: Yup.boolean(),
    other: Yup.string().nullable(),
  })
  .test('at-least-one', errorMessages.SOURCES_OF_FUNDS, (value) => {
    return Boolean(
      value.businessActivity ||
      value.financialAssistance ||
      value.contractAndDerivatives ||
      value.loan ||
      value.saleOfReceivables ||
      value.saleOfSecurities ||
      (value.other && value.other.trim() !== '')
    );
  });
